import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/base/MovieCard";
import { getMovieById, getRelatedMovies } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";

interface MetaRow {
  label: string;
  value: string;
}

export default function Detail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const movie = getMovieById(id);
  const [shareOpen, setShareOpen] = useState(false);

  const related = movie ? getRelatedMovies(movie, 5) : [];

  usePageMeta({
    title: movie ? `${movie.title} - 嫩草影院高清在线看` : "影片不存在 - 嫩草影院",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}）${movie.genres.join("、")}题材影视作品。${movie.synopsis.slice(0, 70)}...`
      : "该影片不存在或已下架，请返回首页浏览其他影视内容。",
    keywords: movie
      ? `${movie.title},${movie.title}在线观看,${movie.genres.join(",")},嫩草影院`
      : "嫩草影院,影视详情",
  });

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 md:pt-[72px]">
          <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
            <span className="w-16 h-16 rounded-full bg-background-200 border border-background-400 flex items-center justify-center text-foreground-500 text-2xl">
              <i className="ri-movie-2-line" />
            </span>
            <h1 className="mt-5 text-xl md:text-2xl font-bold text-foreground-950">
              该影片不存在或已下架
            </h1>
            <p className="mt-2 text-sm text-foreground-600 max-w-md">
              你访问的影片可能已经调整了分类，或者链接地址不完整。可以回到首页继续浏览热门影视内容。
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                className="h-11 px-6 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors whitespace-nowrap"
              >
                <i className="ri-home-4-line" />
                返回首页
              </Link>
              <Link
                to="/category"
                className="h-11 px-6 inline-flex items-center gap-2 rounded-md bg-background-100 hover:bg-background-200 border border-background-400 text-foreground-900 text-sm transition-colors whitespace-nowrap"
              >
                浏览影片分类
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const metaRows: MetaRow[] = [
    { label: "上映时间", value: `${movie.year} 年` },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "地区", value: movie.region },
    { label: movie.type === "movie" ? "片长" : "集数", value: movie.duration },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar overlay />

      <main>
        <section className="relative w-full">
          <div className="absolute inset-0 h-[380px] md:h-[460px] overflow-hidden">
            <img
              src={movie.backdrop}
              alt={`${movie.title} 剧照背景`}
              title={`${movie.title} - 嫩草影院高清在线看`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-50/95 via-foreground-50/80 to-foreground-50/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent" />
          </div>

          <div className="relative w-full px-4 md:px-8 pt-24 md:pt-28 pb-8">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to={`/category?type=${movie.type}`}
                className="hover:text-primary-500 transition-colors"
              >
                {movie.type === "movie" ? "电影" : "电视剧"}
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800 truncate max-w-[200px]">{movie.title}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
              <div className="w-[160px] sm:w-[200px] lg:w-[250px] shrink-0 rounded-lg overflow-hidden border border-background-400/70 bg-background-200">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} - 嫩草影院高清在线看`}
                  className="w-full aspect-[2/3] object-cover object-top"
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2.5 py-1 rounded-sm bg-primary-500 text-white text-[11px]">
                    {movie.type === "movie" ? "电影" : "电视剧"}
                  </span>
                  {movie.genres.map((genre) => (
                    <span
                      key={genre}
                      className="px-2.5 py-1 rounded-sm bg-secondary-100 text-secondary-900 text-[11px]"
                    >
                      {genre}
                    </span>
                  ))}
                </div>

                <h1 className="mt-3 text-2xl md:text-4xl font-black text-foreground-950 leading-tight">
                  {movie.title}
                </h1>
                <p className="mt-1.5 text-xs tracking-[0.22em] uppercase text-foreground-500">
                  {movie.englishTitle}
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-display text-3xl font-semibold text-accent-500">
                      {movie.rating.toFixed(1)}
                    </span>
                    <span className="text-xs text-foreground-500">/ 10 评分</span>
                  </div>
                  <span className="w-px h-4 bg-background-500/60" />
                  <span className="text-xs text-foreground-600">
                    热度 {movie.popularity} · {movie.region} · {movie.year}
                  </span>
                </div>

                <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 max-w-2xl">
                  {metaRows.map((row) => (
                    <div key={row.label} className="flex items-start gap-3 text-sm">
                      <dt className="text-foreground-500 shrink-0 w-16">{row.label}</dt>
                      <dd className="text-foreground-800 leading-6">{row.value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <span className="h-11 px-6 inline-flex items-center gap-2 rounded-md bg-primary-500/20 border border-primary-500/50 text-primary-700 text-sm font-medium">
                    <i className="ri-play-circle-line text-base" />
                    本页为内容预览
                  </span>
                  <button
                    type="button"
                    onClick={() => setShareOpen(true)}
                    className="h-11 px-5 inline-flex items-center gap-2 rounded-md bg-background-100 hover:bg-background-200 border border-background-400 text-foreground-900 text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-share-forward-line text-base" />
                    分享影片
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/category?type=${movie.type}`)}
                    className="h-11 px-5 inline-flex items-center gap-2 rounded-md bg-background-100 hover:bg-background-200 border border-background-400 text-foreground-900 text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-grid-line text-base" />
                    更多同类
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 pt-2 pb-10 md:pb-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <article className="lg:col-span-2 rounded-lg bg-background-100 border border-background-300/80 p-5 md:p-7">
              <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-4 rounded-sm bg-primary-500" />
                剧情简介
              </h2>
              <p className="mt-4 text-sm md:text-[15px] leading-8 text-foreground-700">
                {movie.synopsis}
              </p>
              <h3 className="mt-7 text-sm font-semibold text-foreground-950">演职人员</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="px-3 py-1.5 rounded-md bg-background-200 border border-background-400/60 text-xs text-foreground-800">
                  导演 · {movie.director}
                </span>
                {movie.cast.map((actor) => (
                  <span
                    key={actor}
                    className="px-3 py-1.5 rounded-md bg-background-200 border border-background-400/60 text-xs text-foreground-800"
                  >
                    主演 · {actor}
                  </span>
                ))}
              </div>
              <p className="mt-6 text-xs leading-6 text-foreground-500">
                说明：本站为影视内容演示站点，页面不提供真实播放、下载或收藏服务，影片资料均为虚构示例。
              </p>
            </article>

            <aside className="rounded-lg bg-background-300 border border-background-400/70 p-5 md:p-6">
              <h2 className="text-sm font-bold text-foreground-950">影片信息</h2>
              <ul className="mt-4 space-y-3">
                {metaRows.slice(0, 5).map((row) => (
                  <li key={row.label} className="flex items-start justify-between gap-4 text-xs">
                    <span className="text-foreground-500 shrink-0">{row.label}</span>
                    <span className="text-foreground-800 text-right leading-5">{row.value}</span>
                  </li>
                ))}
                <li className="flex items-start justify-between gap-4 text-xs pt-3 border-t border-background-400/70">
                  <span className="text-foreground-500">内容标识</span>
                  <span className="text-foreground-800 font-mono text-[11px]">{movie.id}</span>
                </li>
              </ul>
              <div className="mt-5 rounded-md bg-background-100 border border-background-400/60 p-3.5">
                <p className="text-xs leading-6 text-foreground-600">
                  <i className="ri-information-line mr-1 text-primary-500" />
                  详情页地址可直接分享，链接中的影片编号与本站片库一一对应。
                </p>
              </div>
            </aside>
          </div>
        </section>

        <section className="w-full bg-background-100/60 border-y border-background-300/70">
          <div className="w-full px-4 md:px-8 py-10 md:py-12">
            <div className="flex items-end justify-between gap-3 mb-6">
              <div>
                <p className="text-[11px] tracking-[0.3em] text-primary-500">YOU MAY ALSO LIKE</p>
                <h2 className="mt-2 text-xl md:text-2xl font-bold text-foreground-950">
                  相关影视推荐
                </h2>
              </div>
              <Link
                to={`/category?type=${movie.type}`}
                className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                查看全部
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {shareOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-foreground-50/70 backdrop-blur-sm animate-fade-in"
          onClick={() => setShareOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg bg-background-100 border border-background-400 p-6 animate-fade-up"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <h3 className="text-base font-bold text-foreground-950">分享《{movie.title}》</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setShareOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-200 text-foreground-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              分享功能为界面演示，暂未接入真实的分享服务。你可以复制浏览器地址栏中的链接，手动发送给朋友。
            </p>
            <p className="mt-3 px-3 py-2 rounded-md bg-background-200 border border-background-400/60 text-xs text-foreground-700 break-all">
              /detail/{movie.id}
            </p>
            <button
              type="button"
              onClick={() => setShareOpen(false)}
              className="mt-5 w-full h-10 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}